import { useCallback, useRef, useState } from "react";

import { postApolloRoute, getApolloCommand } from "../utils/requestApiApollo";
import { Pt } from "../utils/apolloRenderer";

/**
 * 경로 편집/전송 상태 훅(설계 §6.3, §7). 웨이포인트 편집(로컬 좌표) + 경로 전송 + 명령 상태 폴링.
 * 하향 명령은 동기 ack 가 없으므로(설계 §4.3) commandId 로 상태를 폴링해 화면에 그대로 노출한다.
 */

// 명령 생명주기(설계 §7). editing = 전송 전 로컬 편집 단계.
export type CommandStatus = "editing" | "PENDING" | "SENT" | "ACK" | "FAILED" | "EXPIRED";

const POLL_MS = 600;
const POLL_TIMEOUT_MS = 15_000;

export interface ApolloCommandApi {
    waypoints: Pt[];                 // 로컬 프레임 [x, y] (마지막 = 목적지)
    addWaypoint: (x: number, y: number) => void;
    removeLast: () => void;
    clear: () => void;
    sendRoute: (carId: number) => void;
    status: CommandStatus;
    commandId: string | null;
    sending: boolean;
    error: string | null;
}

const TERMINAL: CommandStatus[] = ["ACK", "FAILED", "EXPIRED"];

export function useApolloCommand(): ApolloCommandApi {
    const [waypoints, setWaypoints] = useState<Pt[]>([]);
    const [status, setStatus] = useState<CommandStatus>("editing");
    const [commandId, setCommandId] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPoll = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const addWaypoint = useCallback((x: number, y: number) => setWaypoints((w) => [...w, [x, y]]), []);
    const removeLast = useCallback(() => setWaypoints((w) => w.slice(0, -1)), []);
    const clear = useCallback(() => {
        stopPoll();
        setWaypoints([]);
        setStatus("editing");
        setCommandId(null);
        setError(null);
    }, []);

    const sendRoute = useCallback((carId: number) => {
        if (waypoints.length === 0 || sending) return;
        setSending(true);
        setError(null);
        stopPoll();
        (async () => {
            try {
                const body = waypoints.map((p) => ({ x: p[0], y: p[1] }));
                const data = await postApolloRoute(carId, body, null); // start=null → 차량 현재 위치
                if (data?.result !== "ok" || !data.commandId) {
                    setError(data?.message || "경로 전송에 실패했습니다.");
                    setSending(false);
                    return;
                }
                const id = data.commandId as string;
                setCommandId(id);
                setStatus("PENDING");

                // 명령 상태 폴링(§7 상태 머신 노출)
                const started = Date.now();
                pollRef.current = setInterval(async () => {
                    try {
                        const st = await getApolloCommand(carId, id);
                        const row = st?.command;
                        if (row?.status) setStatus(row.status as CommandStatus);
                        if (row?.status && TERMINAL.includes(row.status)) {
                            if (row.status === "FAILED") setError(row.error || "라우팅 실패");
                            stopPoll();
                            setSending(false);
                        } else if (Date.now() - started > POLL_TIMEOUT_MS) {
                            stopPoll();
                            setSending(false);
                        }
                    } catch (e) {
                        // 일시 오류는 폴링 지속(타임아웃까지)
                    }
                }, POLL_MS);
            } catch (e) {
                setError("경로 전송 중 오류가 발생했습니다.");
                setSending(false);
            }
        })();
    }, [waypoints, sending]);

    return { waypoints, addWaypoint, removeLast, clear, sendRoute, status, commandId, sending, error };
}
